import { Controller } from '@hotwired/stimulus'

/*
 * Marca el enlace de la sección que se está leyendo. El diseño resalta el
 * bloque activo en la barra de anclas, y sin esto la barra se queda siempre
 * con el primero marcado.
 *
 * Se observa la sección, no la posición del scroll: un listener de scroll
 * recalcularía en cada píxel y esta página es larga.
 *
 * La clase que marca el activo la pone cada barra (`data-ficha-anclas-activo-class`):
 * la de la ficha de acción y la del hero de los productos no comparten estilos.
 */
export default class extends Controller {
  static targets = ['enlace']
  static classes = ['activo']

  connect () {
    this.secciones = new Map()
    // Qué secciones están ahora mismo dentro de la banda de lectura. Se guarda
    // en un conjunto porque cada aviso del observador trae SOLO las que han
    // cambiado, no la foto completa.
    this.visibles = new Set()

    // `rootMargin` superior negativo: la sección cuenta como activa cuando
    // llega al tercio superior, no cuando asoma por abajo.
    this.observador = new IntersectionObserver(
      entradas => this.marcarVisible(entradas),
      { rootMargin: '-33% 0px -60% 0px', threshold: 0 }
    )

    const pendientes = this.enlaceTargets.filter(enlace => !this.observar(enlace))
    // Una ficha sin foro ni dividendos no pinta esas secciones: el enlace
    // sobra en vez de quedarse muerto.
    pendientes.forEach(enlace => { enlace.hidden = true })
    this.esperarSecciones(pendientes)
  }

  disconnect () {
    this.observador?.disconnect()
    this.observador = null
    this.vigia?.disconnect()
    this.vigia = null
  }

  /*
   * Empareja el enlace con su sección y empieza a observarla. Devuelve si la
   * sección está en el documento.
   */
  observar (enlace) {
    const seccion = document.getElementById(enlace.dataset.ancla)
    if (!seccion) return false

    this.secciones.set(seccion, enlace)
    this.observador.observe(seccion)
    return true
  }

  /*
   * Hay secciones que llegan después que la barra: en móvil el bloque del blog
   * de las fichas de producto se pide por AJAX al acercarse (`lazyload-div`).
   * Si se escondiera el enlace para siempre, la barra perdería una sección que
   * sí acaba en la página. Se vigila el documento solo mientras quede alguna
   * por llegar, y su enlace reaparece cuando llega.
   */
  esperarSecciones (pendientes) {
    if (!pendientes.length) return

    let porLlegar = pendientes
    this.vigia = new MutationObserver(() => {
      porLlegar = porLlegar.filter(enlace => {
        if (!this.observar(enlace)) return true

        enlace.hidden = false
        return false
      })
      if (!porLlegar.length) this.vigia.disconnect()
    })
    this.vigia.observe(document.body, { childList: true, subtree: true })
  }

  marcarVisible (entradas) {
    entradas.forEach(entrada => {
      if (entrada.isIntersecting) this.visibles.add(entrada.target)
      else this.visibles.delete(entrada.target)
    })

    const activo = this.secciones.get(this.seccionActiva())
    this.enlaceTargets.forEach(enlace => {
      enlace.classList.toggle(this.activoClass, enlace === activo)
    })
  }

  /*
   * La sección que manda en la barra.
   *
   * Si hay varias en la banda gana la primera del documento, no la primera que
   * avisó: el observador entrega las entradas en el orden en que se registraron
   * los cambios, y con `find` una sección de más abajo podía adelantarse.
   *
   * Y cuando NO hay ninguna en la banda —arriba del todo, entre dos secciones,
   * o en el pie— antes se salía sin tocar nada y la barra se quedaba con la
   * marca vieja: al volver arriba seguía resaltada la sección que se leyó por
   * última vez. Manda entonces la última sección ya recorrida, y por encima de
   * la primera, la primera.
   *
   * El orden es el del documento y no el de los enlaces: en el hero de los
   * productos la barra no sigue el orden de la página («Artículos» va antes que
   * «FAQ» y en la ficha sale después), y una sección que llega tarde (ver
   * `esperarSecciones`) entra al final del mapa aunque esté más arriba.
   *
   * Una sección puede ir DENTRO de otra: «Otros planes» de los seguros y
   * «Quiénes somos» de los vendibles están en `#datos-producto`. Mientras se lee
   * una, las dos están en la banda y la de fuera es la primera del documento,
   * así que su pestaña no se marcaba nunca. Entre las de la banda manda la más
   * interior.
   */
  seccionActiva () {
    const enOrden = [...this.secciones.keys()].sort(
      (a, b) => a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    )

    const enLaBanda = enOrden.filter(seccion => this.visibles.has(seccion))
    const interior = enLaBanda.find(
      seccion => !enLaBanda.some(otra => otra !== seccion && seccion.contains(otra))
    )
    if (interior) return interior

    const recorridas = enOrden.filter(seccion => seccion.getBoundingClientRect().top < 0)
    return recorridas.length ? recorridas[recorridas.length - 1] : enOrden[0]
  }
}
